import React from 'react';
import { motion } from 'framer-motion';
import { ArrowDown, Check } from 'lucide-react';
import { usePreloadImage } from '../../hooks/useResourceHints';
import type { HeroContent } from '../../types/salesPage';
interface SalesHeroProps {
  content: HeroContent;
}
export function SalesHero({
  content
}: SalesHeroProps) {
  const {
    mast,
    kicker,
    headline,
    subheadline,
    proofPoints,
    ctaLabel,
    ctaNote,
    image
  } = content;
  usePreloadImage(image.src);
  return <header className="relative w-full overflow-hidden bg-ink" data-id="element-242" data-mp="24ue3tywk2wnf">
      <img src={image.src} alt={image.alt} loading="eager" decoding="async" width={1600} height={900} className="absolute inset-0 h-full w-full object-cover object-[70%_center] opacity-40 md:opacity-55" data-id="element-243" data-mp="29q2xjllw9jbt" />
      <div aria-hidden="true" className="absolute inset-0 bg-ink/70 md:bg-[linear-gradient(90deg,rgba(8,9,11,0.95)_0%,rgba(8,9,11,0.78)_50%,rgba(8,9,11,0.35)_100%)]" data-id="element-244" data-mp="2nqngchrt7f5b" />
      <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-[55%] bg-[linear-gradient(0deg,#0B0D10_8%,rgba(11,13,16,0.6)_60%,transparent_100%)]" data-id="element-245" data-mp="4c8wtikz69xw" />
      <div aria-hidden="true" className="grain-overlay absolute inset-0 opacity-60" data-id="element-246" data-mp="3vgyvx41ccphc" />

      <div className="relative z-10 mx-auto max-w-5xl px-5 pb-12 pt-8 text-center sm:px-6 md:pb-20 md:pt-14" data-id="element-247" data-mp="e4enmozhqlbi">
        <p className="font-mono text-[10px] font-semibold tracking-[0.24em] text-gold md:text-[11px]" data-id="element-248" data-mp="99sb2ihoampu">
          {mast}
        </p>

        <motion.p initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        ease: 'easeOut'
      }} className="mt-6 inline-flex items-center gap-2.5 border border-gold/30 bg-gold/[0.06] px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-gold-soft md:text-[10px]" data-id="element-249" data-mp="j1y29q03p03z">
          {kicker}
        </motion.p>

        <motion.h1 initial={{
        opacity: 0,
        y: 18
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.75,
        delay: 0.08,
        ease: 'easeOut'
      }} className="mt-5 font-serif text-[28px] font-bold leading-[1.14] text-parchment sm:text-[36px] md:text-[50px] md:leading-[1.08]" data-id="element-250" data-mp="2ttziv33zow9r">
          {headline.before}
          <span className="italic text-gold-soft" data-id="element-251" data-mp="16pomh9ekayb6">{headline.accent}</span>
          {headline.after}
        </motion.h1>

        <motion.p initial={{
        opacity: 0,
        y: 18
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.75,
        delay: 0.16,
        ease: 'easeOut'
      }} className="mx-auto mt-5 max-w-2xl text-[14.5px] font-light leading-[1.65] text-[#AEB2B8] md:text-[17px]" data-id="element-252" data-mp="1bnq6bwjtb1np">
          {subheadline}
        </motion.p>

        <motion.ul initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        delay: 0.3
      }} className="mt-7 flex flex-wrap items-center justify-center gap-x-5 gap-y-2" data-id="element-253" data-mp="1ufiwzkbesas9">
          {proofPoints.map(point => <li key={point} className="flex items-center gap-2 font-mono text-[9.5px] uppercase tracking-[0.1em] text-ash md:text-[10.5px]" data-id="element-254" data-mp="2ej82wvgmumd5">
              <Check aria-hidden="true" className="h-3.5 w-3.5 text-gold" data-id="element-255" data-mp="24kix4120gsd9" />
              {point}
            </li>)}
        </motion.ul>

        <motion.div initial={{
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7,
        delay: 0.4,
        ease: 'easeOut'
      }} className="mt-9 flex flex-col items-center" data-id="element-256" data-mp="20k8oshqqxpv8">
          <a href="#oferta" className="group inline-flex items-center gap-2.5 rounded-sm bg-gold px-7 py-4 font-mono text-[11px] font-bold uppercase tracking-[0.12em] text-[#181205] shadow-[0_16px_34px_rgba(200,151,63,0.25)] transition-colors duration-200 hover:bg-gold-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-soft focus-visible:ring-offset-2 focus-visible:ring-offset-ink md:text-[12.5px]" data-id="element-257" data-mp="2scfuwo8mljvd">
            {ctaLabel}
            <ArrowDown aria-hidden="true" className="h-4 w-4 transition-transform duration-200 group-hover:translate-y-0.5" data-id="element-258" data-mp="2vpmrlp7e6e7k" />
          </a>
          <p className="mt-3 font-mono text-[9px] uppercase tracking-[0.12em] text-ash/70 md:text-[10px]" data-id="element-259" data-mp="2mrkw1p6fgdro">
            {ctaNote}
          </p>
        </motion.div>
      </div>
    </header>;
}